import type {
  CustomSEO,
  CustomSEOProps,
  SEOPageType,
} from '@iheartjane/data-access';
import {
  AddIcon,
  Button,
  InfoIcon,
  Popover,
  Typography,
  Flex,
} from '@iheartjane/reefer';
import { Form, useFormContext, useWatch } from '@iheartjane/reefer-hook-form';
import { useCallback } from 'react';

import { MaxCharacterHelper } from '../components/MaxCharacterHelper';
import { useSeoForm } from '../providers/SeoFormProvider';
import { SEO_VARIABLES } from './defaultSeoConstants';
import { PreviewSeo } from './PreviewSeo';

const VARIABLE_REGEX = /\{\{\s*([^{}]+?)\s*\}\}/g;

// Any brace left after removing well-formed {{ variable }} tokens is malformed
const hasMalformedBraces = (value: string) =>
  /[{}]/.test(value.replace(VARIABLE_REGEX, ''));

interface SeoFormProps {
  descriptionHint: string;
  faviconUrl?: string | null;
  menuUrl?: string;
  pageType: CustomSEOProps;
  titleHint: string;
  variables: Record<string, string | undefined>;
}

const FieldLabel = ({ label, hint }: { hint: string; label: string }) => (
  <Flex alignItems="center">
    {label}
    {/* Stops the surrounding <label> from forwarding the click to the input, which would close the popover */}
    <span role="presentation" onClick={(event) => event.preventDefault()}>
      <Popover
        openOn="hover"
        label={`${label} more info`}
        target={<InfoIcon size="sm" ml={8} />}
      >
        <Popover.Content label={`${label} more info`}>
          <Typography width="420px">{hint}</Typography>
        </Popover.Content>
      </Popover>
    </span>
  </Flex>
);

const VariableButtons = ({
  fieldLabel,
  fieldName,
  variables,
}: {
  fieldLabel: string;
  fieldName: string;
  variables: string[];
}) => {
  const { insertVariable } = useSeoForm();

  return (
    <Flex
      aria-label={`Variables for ${fieldLabel}`}
      flexDirection="column"
      gap={16}
      role="group"
    >
      <Typography color="grays-mid">
        Variables for {fieldLabel} — click to insert
      </Typography>
      {/* Keep focus (and the cursor) in the field so the variable lands where the user was typing */}
      <div onMouseDown={(event) => event.preventDefault()} role="presentation">
        <Flex gap={16} flexWrap="wrap">
          {variables.map((variable) => (
            <Button
              key={variable}
              variant="secondary"
              startIcon={<AddIcon size="sm" />}
              label={`{{ ${variable} }}`}
              onClick={() => insertVariable(fieldName, variable)}
            />
          ))}
        </Flex>
      </div>
    </Flex>
  );
};

export const SeoForm = ({
  descriptionHint,
  faviconUrl,
  menuUrl,
  pageType,
  titleHint,
  variables,
}: SeoFormProps) => {
  const { control } = useFormContext<CustomSEO>();
  const onPage = pageType.includes('onPage');
  const titleField = `${pageType}.title` as const;
  const descriptionField = `${pageType}.description` as const;
  const [title, description] = useWatch({
    control,
    name: [titleField, descriptionField],
  });
  const pageVariables = onPage
    ? SEO_VARIABLES['category']
    : SEO_VARIABLES[pageType as SEOPageType];

  const validateField = useCallback(
    (value: string): string | true => {
      const stringValue = typeof value === 'string' ? value : '';

      if (!stringValue) {
        return true;
      }

      if (hasMalformedBraces(stringValue)) {
        return 'Invalid curly braces. Use double curly braces like {{ variableName }}';
      }

      const variableMatches = [...stringValue.matchAll(VARIABLE_REGEX)];
      const invalidVariables = variableMatches
        .map((match) => match[1].trim())
        .filter((variable) => !pageVariables.includes(variable));

      if (invalidVariables.length > 0) {
        const errorMessage = `"${invalidVariables[0]}" is not a valid variable`;
        return errorMessage;
      }

      return true;
    },
    [pageVariables]
  );

  return (
    <Flex flexDirection="column" gap={24}>
      <Form.TextField
        name={titleField}
        label={<FieldLabel label="Page title" hint={titleHint} />}
        required={!onPage}
        shouldUnregister={false}
        validate={validateField}
        helperText={
          onPage ? (
            <MaxCharacterHelper value={title ?? ''} maxLength={70} />
          ) : null
        }
      />
      <VariableButtons
        fieldLabel="page title"
        fieldName={titleField}
        variables={pageVariables}
      />

      <Form.TextAreaField
        name={descriptionField}
        label={<FieldLabel label="Page description" hint={descriptionHint} />}
        required={!onPage}
        shouldUnregister={false}
        validate={validateField}
        helperText={
          onPage ? (
            <MaxCharacterHelper value={description ?? ''} maxLength={500} />
          ) : null
        }
      />
      <VariableButtons
        fieldLabel="page description"
        fieldName={descriptionField}
        variables={pageVariables}
      />

      <PreviewSeo
        faviconUrl={faviconUrl}
        menuUrl={menuUrl}
        title={title || 'Page title'}
        description={description || 'Page description'}
        variables={variables}
      />
    </Flex>
  );
};
